import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Container,
  Row,
  Col,
  Input,
  Button,
  Badge,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import AdminHeader from "components/Headers/AdminHeader";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import { getAttendanceReport } from "action/report";
import { attendanceReport } from "constants/report";

function AttendanceReport() {
  const initialFilters = {
    dateRange: [null, null],
    course: "",
    batch: "",
    trainee: "",
  };

  const dispatch = useDispatch();

  const attendanceReportData = useSelector(
    (state) => state.report.attendanceReport,
  );

  const [filters, setFilters] = useState(initialFilters);

  const [filterState, setFilterState] = useState({
    filterParams: [],
  });

  const [pageNum, setPageNum] = useState(1);

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  useEffect(() => {
    getAttendanceReport(dispatch, filterState.filterParams, pageNum);

    return () => {
      dispatch({
        type: attendanceReport,
        payload: {},
      });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (attendanceReportData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: attendanceReportData?.data?.attendanceReport?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (attendanceReportData?.code || attendanceReportData?.message) {
      setListState({
        isLoading: false,
        error:
          attendanceReportData?.message || "Failed to load attendance report.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [attendanceReportData]);

  const formatDate = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.course?.trim()) {
      filterParams.push({
        field: "CourseName",
        condition: "like",
        filterValues: [filters.course.trim()],
      });
    }

    if (filters.batch?.trim()) {
      filterParams.push({
        field: "BatchName",
        condition: "like",
        filterValues: [filters.batch.trim()],
      });
    }

    if (filters.trainee?.trim()) {
      filterParams.push({
        field: "TraineeName",
        condition: "like",
        filterValues: [filters.trainee.trim()],
      });
    }

    const [startDate, endDate] = filters.dateRange;

    if (startDate && endDate) {
      filterParams.push({
        field: "AttendanceDate",
        condition: "btw",
        filterValues: [formatDate(startDate), formatDate(endDate)],
      });
    } else if (startDate) {
      filterParams.push({
        field: "AttendanceDate",
        condition: "eq",
        filterValues: [formatDate(startDate)],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((previous) => ({
        ...previous,
        isLoading: true,
        error: "",
        showLoaderOnClick: true,
      }));

      setFilterState({
        filterParams: newFilterParams,
      });

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const clearSearch = () => {
    setListState((previous) => ({
      ...previous,
      isLoading: true,
      error: "",
      showLoaderOnClick: false,
    }));

    setFilters(initialFilters);

    setFilterState({
      filterParams: [],
    });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 70,
      maxWidth: 80,
      centerAlign: true,
    },
    {
      Header: "Trainee",
      accessor: "trainee",
      minWidth: 170,
      maxWidth: 220,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Course",
      accessor: "course",
      minWidth: 220,
      maxWidth: 280,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Batch",
      accessor: "batch",
      minWidth: 180,
      maxWidth: 240,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Attendance Date",
      accessor: "attendanceDate",
      minWidth: 150,
      maxWidth: 170,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Status",
      accessor: "status",
      centerAlign: true,
      minWidth: 100,
      maxWidth: 120,
      Cell: ({ value }) => (
        <Badge
          color={
            value === "PRESENT"
              ? "success"
              : value === "ABSENT"
                ? "warning"
                : "secondary"
          }
          pill
        >
          {value || "-"}
        </Badge>
      ),
    },
    {
      Header: "Attendance Percentage",
      accessor: "attendancePercentage",
      minWidth: 170,
      maxWidth: 190,
      centerAlign: true,
      Cell: ({ value }) => `${value ?? 0}%`,
    },
  ];

  const tableData = (attendanceReportData?.data?.attendanceReport || []).map(
    (item, index) => ({
      ...item,
      id: `${item?.courseID}-${item?.batchID}-${item?.traineeID}-${item?.attendanceDate}-${index}`,
      srNo:
        (pageNum - 1) *
          (attendanceReportData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      trainee: item?.traineeName || "-",
      course: item?.courseName || "-",
      batch: item?.batchName || "-",
      attendanceDate: item?.attendanceDate || "-",
      status: item?.attendanceStatus || "-",
      attendancePercentage: item?.attendancePercentage ?? 0,
    }),
  );

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0 text-default">
                  Attendance Report
                </CardTitle>
              </CardHeader>

              <CardBody>
                {/* Filters */}
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      name="trainee"
                      placeholder="Search by trainee"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.trainee}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      name="course"
                      placeholder="Search by course"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.course}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      name="batch"
                      placeholder="Search by batch"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.batch}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <DatePicker
                      selectsRange
                      startDate={filters.dateRange[0]}
                      endDate={filters.dateRange[1]}
                      onChange={(update) =>
                        setFilters((previous) => ({
                          ...previous,
                          dateRange: update,
                        }))
                      }
                      isClearable
                      placeholderText="Select attendance date or range"
                      className="form-control mr-2 mb-2 filter-input-large"
                      dateFormat="dd-MMM-yyyy"
                      showMonthDropdown
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={100}
                      calendarClassName="custom-calendar"
                      onPaste={(event) => event.preventDefault()}
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      disabled={listState.isLoading}
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${
                        listState.showLoaderOnClick ? "search-btn" : ""
                      }`}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Record Count */}
                <div className="mt-2 mb-3 mx-1">
                  {attendanceReportData?.data?.totalRecords > 0 && (
                    <>
                      {filterState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing{" "}
                          {attendanceReportData?.data?.totalFilteredRecords ||
                            0}{" "}
                          of {attendanceReportData?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records:{" "}
                          {attendanceReportData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {/* Table States */}
                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : listState.error ? (
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i className="fa-solid fa-circle-exclamation" />
                    </div>
                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords || tableData.length === 0 ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info" />
                    </div>
                    <span>No attendance records found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={tableData}
                      defaultPageSize={
                        attendanceReportData?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE
                      }
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        (attendanceReportData?.data?.totalFilteredRecords ||
                          0) /
                          (attendanceReportData?.data?.noOfRecordsPerPage ||
                            DEFAULT_RECORDS_PER_PAGE),
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AttendanceReport;
